<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, onMounted, onUnmounted, ref } from "vue";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { useQuarantineStore } from "@/stores/quarantine-store";

import { $can, fromUtc } from "@/services/UtilitiesService";

import { useRootLayout } from "@/hooks/use-root-layout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

const $router = useRouter();
const routeParams = useCaseRouteParams();

const timerId = ref<NodeJS.Timeout>();
const hasFiles = computed(() => {
  return useQuarantineStore().hasFiles;
});
const files = computed(() => {
  return useQuarantineStore().entries;
});

const { sidebarRef } = useRootLayout();

function backToCase(): void {
  $router.push({
    name: "case",
    params: { brand: routeParams.value.brand, casename: routeParams.value.casename },
  });
}

onMounted(() => {
  if ($can("upload")) return;
  $router.push({ name: "error", params: { brand: routeParams.value.brand, code: "403" } });
});

onMounted(() => {
  const onTimeout = async () => {
    await useQuarantineStore().refresh();
    timerId.value = setTimeout(() => void onTimeout(), 10e3);
  };

  useQuarantineStore()
    .load()
    .then(() => {
      timerId.value = setTimeout(() => void onTimeout(), 30e3);
    });
});

onUnmounted(() => clearTimeout(timerId.value));
</script>
<template>
  <Teleport :to="sidebarRef">
    <StringTooltip side="right" item="Back to Case" #="{ item }">
      <button
        type="button"
        class="tw-btn-md-white tw-grid tw-place-items-center tw-rounded-md tw-bg-gray-300 tw-px-3 tw-py-1.5 tw-text-gray-800"
        :aria-label="item"
        @click.prevent="backToCase"
      >
        <i class="fas fa-arrow-left"></i>
      </button>
    </StringTooltip>
  </Teleport>
  <div class="tw-flex tw-p-4 tw-shadow-inner">
    <h1 class="tw-sr-only">Main Content</h1>
    <div
      tabindex="0"
      class="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-gap-3 tw-rounded-sm tw-bg-white tw-p-2"
    >
      <table v-if="hasFiles" class="tw-w-full tw-rounded-md tw-border">
        <thead>
          <tr tabindex="0" class="z-1 tw-sticky -tw-top-1 tw-h-9 tw-divide-x tw-border-b">
            <th
              v-for="name in [
                'File Name',
                'Uploaded By',
                'Uploaded On',
                'Last Requested',
                'Status',
                'Reason for Rejection',
              ]"
              :key="name"
              scope="col"
              class="tw-h-7 tw-min-w-32 tw-max-w-sm tw-bg-gray-100 tw-p-2 [&>*]:tw-p-2"
            >
              {{ name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(file, index) in files" :key="`${file.fileReference}-${index}`">
            <tr
              :class="{ 'tw-bg-gray-200': index % 2 === 0 }"
              class="tw-h-7 tw-divide-x tw-border-b last:tw-border-b-0 [&>*]:tw-p-2 [&>*]:hover:tw-bg-gray-300 [&>*]:focus:tw-bg-gray-300"
            >
              <th scope="row" class="file-name tw-p-1" :title="file.edxFileName">
                {{ file.fileName }}
              </th>
              <td class="tw-p-1">{{ file.uploadedBy }}</td>
              <td class="tw-p-1">{{ fromUtc(file.uploadedOn, { timeStyle: "medium" }) }}</td>
              <td class="tw-p-1">
                {{ fromUtc(file.lastRequested, { timeStyle: "medium" }) }}
              </td>
              <td class="tw-p-1">{{ file.statusText }}</td>
              <td colspan="5" class="tw-p-1">
                <span>
                  {{ file.isInStatus("Rejected") ? file.getRejectionReason : "None" }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <h2 v-else>There are no files in the quarantine</h2>
    </div>
  </div>
</template>
<style lang="scss">
.file-name {
  word-break: break-all;
}
</style>
